// src/components/ProducerInformation.js
/**
 * Producer Information Component
 * Displays detailed producer profiles with maps, history, and regional information
 */

import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import "./ProducerInformation.css";
import "./ProducerInformation.css";

const ProducerInformation = ({ beverage, showMap = true, compact = false }) => {
  const [producerData, setProducerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [mapLoaded, setMapLoaded] = useState(false);

  useEffect(() => {
    if (beverage?.aiEnhanced?.producer) {
      setProducerData(beverage.aiEnhanced.producer);
      setLoading(false);
    } else {
      setLoading(false);
    }
  }, [beverage]);

  const formatFoundedYear = (founded) => {
    if (!founded) return "Unknown";
    return founded;
  };

  const getRegionDisplayName = (location) => {
    if (!location) return "Unknown Region";

    const parts = [
      location.subregion,
      location.region,
      location.country,
    ].filter(Boolean);

    return parts.join(", ");
  };

  const getCategorySpecificInfo = () => {
    if (!beverage?.aiEnhanced?.categoryDetails) return null;

    const category = beverage.category?.toLowerCase();
    const details = beverage.aiEnhanced.categoryDetails[category];

    if (!details) return null;

    switch (category) {
      case "wine":
        return {
          type: "Vineyard",
          specificInfo: {
            Terroir: details.terroir
              ? `${details.terroir.soil}, ${details.terroir.climate}`
              : "Not specified",
            Elevation: details.terroir?.elevation || "Not specified",
            "Primary Varietals":
              details.varietal?.join(", ") || "Not specified",
            Vineyard: details.vineyard || "Not specified",
          },
        };

      case "beer":
        return {
          type: "Brewery",
          specificInfo: {
            "Beer Style": details.style || "Not specified",
            ABV: details.abv ? `${details.abv}%` : "Not specified",
            IBU: details.ibu || "Not specified",
            Seasonality: details.seasonality || "Year-round",
          },
        };

      case "spirit":
        return {
          type: "Distillery",
          specificInfo: {
            "Spirit Type": details.type || "Not specified",
            Age: details.age || "Not specified",
            Proof: details.proof ? `${details.proof} proof` : "Not specified",
            Distillation: details.distillation?.method || "Not specified",
          },
        };

      default:
        return {
          type: "Producer",
          specificInfo: {},
        };
    }
  };

  const getTechniques = (techniques) => {
    if (!techniques || techniques.length === 0) return [];

    const techniqueLabels = {
      organic: { label: "Organic", color: "#28a745", icon: "🌱" },
      biodynamic: { label: "Biodynamic", color: "#17a2b8", icon: "🌙" },
      traditional: {
        label: "Traditional Methods",
        color: "#6f42c1",
        icon: "🏛️",
      },
      sustainable: { label: "Sustainable", color: "#20c997", icon: "♻️" },
      "small-batch": { label: "Small Batch", color: "#fd7e14", icon: "👨‍🍳" },
    };

    return techniques.map(
      (technique) =>
        techniqueLabels[technique] || {
          label: technique,
          color: "#6c757d",
          icon: "⭐",
        }
    );
  };

  if (loading) {
    return (
      <div className={`producer-information ${compact ? "compact" : ""}`}>
        <div className="producer-loading">
          <div className="loading-spinner"></div>
          <span>Loading producer information...</span>
        </div>
      </div>
    );
  }

  if (!producerData || !producerData.name) {
    return (
      <div className={`producer-information ${compact ? "compact" : ""}`}>
        <div className="no-producer-info">
          <span className="no-info-icon">🏢</span>
          <p>Producer information not yet available</p>
          {!compact && (
            <small>
              Producer details will be added as our AI gathers more information
            </small>
          )}
        </div>
      </div>
    );
  }

  const categoryInfo = getCategorySpecificInfo();
  const techniques = getTechniques(producerData.techniques);

  if (compact) {
    return (
      <div className="producer-information compact">
        <div className="producer-summary">
          <div className="producer-basic">
            <h4>{producerData.name}</h4>
            <p className="location">
              {getRegionDisplayName(producerData.location)}
            </p>
            <p className="founded">
              Est. {formatFoundedYear(producerData.founded)}
            </p>
          </div>
          {techniques.length > 0 && (
            <div className="techniques-compact">
              {techniques.slice(0, 2).map((technique, index) => (
                <span
                  key={index}
                  className="technique-badge compact"
                  style={{ backgroundColor: technique.color }}
                >
                  {technique.icon} {technique.label}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="producer-information">
      <div className="producer-header">
        <div className="producer-title">
          <h3>
            {categoryInfo?.type || "Producer"}: {producerData.name}
          </h3>
          <div className="producer-subtitle">
            <span className="location-name">
              {getRegionDisplayName(producerData.location)}
            </span>
            <span className="founded-year">
              Est. {formatFoundedYear(producerData.founded)}
            </span>
          </div>
        </div>

        {producerData.website && (
          <a
            href={producerData.website}
            target="_blank"
            rel="noopener noreferrer"
            className="website-link"
          >
            Visit Website 🔗
          </a>
        )}
      </div>

      <div className="producer-content">
        <div className="content-tabs">
          <button
            className={`tab-button ${activeTab === "overview" ? "active" : ""}`}
            onClick={() => setActiveTab("overview")}
          >
            Overview
          </button>
          <button
            className={`tab-button ${activeTab === "details" ? "active" : ""}`}
            onClick={() => setActiveTab("details")}
          >
            Details
          </button>
          {producerData.history && (
            <button
              className={`tab-button ${
                activeTab === "history" ? "active" : ""
              }`}
              onClick={() => setActiveTab("history")}
            >
              History
            </button>
          )}
          {showMap && producerData.location?.coordinates && (
            <button
              className={`tab-button ${
                activeTab === "location" ? "active" : ""
              }`}
              onClick={() => setActiveTab("location")}
            >
              Location
            </button>
          )}
        </div>

        <div className="tab-content">
          {activeTab === "overview" && (
            <div className="overview-tab">
              {producerData.philosophy && (
                <div className="philosophy-section">
                  <h4>Philosophy</h4>
                  <p>{producerData.philosophy}</p>
                </div>
              )}

              {techniques.length > 0 && (
                <div className="techniques-section">
                  <h4>Production Methods</h4>
                  <div className="techniques-grid">
                    {techniques.map((technique, index) => (
                      <div
                        key={index}
                        className="technique-badge"
                        style={{ backgroundColor: technique.color }}
                      >
                        <span className="technique-icon">{technique.icon}</span>
                        <span className="technique-label">
                          {technique.label}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {producerData.accolades && producerData.accolades.length > 0 && (
                <div className="accolades-section">
                  <h4>Awards & Recognition</h4>
                  <div className="accolades-list">
                    {producerData.accolades
                      .slice(0, 5)
                      .map((accolade, index) => (
                        <div key={index} className="accolade-item">
                          <span className="accolade-icon">🏆</span>
                          <span className="accolade-text">{accolade}</span>
                        </div>
                      ))}
                    {producerData.accolades.length > 5 && (
                      <p className="more-accolades">
                        ...and {producerData.accolades.length - 5} more awards
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === "details" && (
            <div className="details-tab">
              <div className="details-grid">
                <div className="detail-section">
                  <h4>Basic Information</h4>
                  <div className="detail-items">
                    <div className="detail-item">
                      <span className="detail-label">Founded</span>
                      <span className="detail-value">
                        {formatFoundedYear(producerData.founded)}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Location</span>
                      <span className="detail-value">
                        {getRegionDisplayName(producerData.location)}
                      </span>
                    </div>
                    {producerData.location?.appellation && (
                      <div className="detail-item">
                        <span className="detail-label">Appellation</span>
                        <span className="detail-value">
                          {producerData.location.appellation}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {categoryInfo && (
                  <div className="detail-section">
                    <h4>{categoryInfo.type} Specifics</h4>
                    <div className="detail-items">
                      {Object.entries(categoryInfo.specificInfo).map(
                        ([key, value]) => (
                          <div key={key} className="detail-item">
                            <span className="detail-label">{key}</span>
                            <span className="detail-value">{value}</span>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === "history" && producerData.history && (
            <div className="history-tab">
              <div className="history-content">
                <h4>Heritage & History</h4>
                <div className="history-text">
                  {producerData.history.split("\n").map((paragraph, index) => (
                    <p key={index}>{paragraph}</p>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === "location" && producerData.location?.coordinates && (
            <div className="location-tab">
              <div className="location-content">
                <div className="map-placeholder">
                  <div className="map-info">
                    <h4>📍 Location</h4>
                    <p>
                      <strong>Region:</strong>{" "}
                      {getRegionDisplayName(producerData.location)}
                    </p>
                    <p>
                      <strong>Coordinates:</strong>{" "}
                      {producerData.location.coordinates.lat},{" "}
                      {producerData.location.coordinates.lng}
                    </p>
                    <div className="map-actions">
                      <button
                        className="map-action-button"
                        onClick={() => {
                          const coords = producerData.location.coordinates;
                          window.open(
                            `https://www.google.com/maps?q=${coords.lat},${coords.lng}`,
                            "_blank"
                          );
                        }}
                      >
                        View on Google Maps 🗺️
                      </button>
                    </div>
                  </div>
                  <div className="map-visual">
                    {/* In a real implementation, this would be an actual map component */}
                    <div className="map-mockup">
                      <div className="map-marker">📍</div>
                      <div className="map-region">
                        {producerData.location.region}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

ProducerInformation.propTypes = {
  beverage: PropTypes.shape({
    category: PropTypes.string,
    aiEnhanced: PropTypes.shape({
      producer: PropTypes.shape({
        name: PropTypes.string,
        founded: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        location: PropTypes.shape({
          country: PropTypes.string,
          region: PropTypes.string,
          subregion: PropTypes.string,
          coordinates: PropTypes.shape({
            lat: PropTypes.number,
            lng: PropTypes.number,
          }),
        }),
        history: PropTypes.string,
        philosophy: PropTypes.string,
        techniques: PropTypes.arrayOf(PropTypes.string),
        accolades: PropTypes.arrayOf(PropTypes.string),
      }),
      categoryDetails: PropTypes.object,
    }),
  }),
  showMap: PropTypes.bool,
  compact: PropTypes.bool,
};

ProducerInformation.defaultProps = {
  beverage: null,
  showMap: true,
  compact: false,
};

export default ProducerInformation;
